import React, { useEffect, useMemo, useState } from 'react';
import { Lock, ChevronDown, Sparkles, TrendingUp, Clock } from 'lucide-react';
import { useLang } from '../LangContext';
import { PRESALE_PHASES, PAY_CURRENCIES, getBonusPercent, getNextTier, getPresalePhaseStatus, formatCountdown, PRESALE_START } from '../presaleConfig';
import { USDC_ICON, USDT_ICON } from '../mock';
import { presaleApi } from '../api';
import DepositModal from './DepositModal';
import SupportButton from './SupportButton';

const PromoCountdown = ({ endsAt, now }) => {
  const diff = Math.max(0, Math.floor((endsAt - now) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return (
    <div className="text-base font-black text-amber-700 tabular-nums">
      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </div>
  );
};

const Presale = () => {
  const { t, lang } = useLang();
  const [amount, setAmount] = useState(10);
  const [currency, setCurrency] = useState('USDC');
  const [depositOpen, setDepositOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [backendState, setBackendState] = useState(null);

  // OFFICIAL PRESALE LAUNCH — HARDCODED & LOCKED.
  // 2026-07-01 04:00 UTC = midnight (00:00) local time in Haiti (UTC-4).
  // Identical countdown for everyone worldwide. Do NOT replace with a backend
  // value or env var — this is intentionally fixed.
  const LAUNCH_DATE = useMemo(() => new Date('2026-07-01T04:00:00Z'), []);
  const launchLabel = lang === 'fr' ? '1ER JUILLET' : lang === 'es' ? '1 DE JULIO' : 'JULY 1';

  // Tick every second for countdown.
  // Browsers throttle setInterval when the tab is in background — some users
  // reported the countdown appearing "frozen" when they came back. We force
  // an immediate update on visibilitychange & focus events so it always resyncs.
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = setInterval(tick, 1000);
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', tick);
    };
  }, []);

  // Load backend presale state (min deposit + promo + start date)
  useEffect(() => {
    let alive = true;
    presaleApi.state().then(s => { if (alive) setBackendState(s); }).catch(() => {});
    // Poll every 5s so the progress bar updates in near real-time as deposits come in
    const id = setInterval(() => { presaleApi.state().then(s => { if (alive) setBackendState(s); }).catch(() => {}); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const minDeposit = backendState?.min_deposit_usd ?? 10;
  const promoActive = !!backendState?.promo_active;
  const presaleStarted = !!backendState?.presale_started;
  const presaleStartsAt = backendState?.presale_starts_at ? new Date(backendState.presale_starts_at) : null;
  const promoEndsAt = backendState?.promo_ends_at ? new Date(backendState.promo_ends_at) : null;

  // ===== SERVER-SYNCED PHASE COUNTDOWN =====
  // Phase auto-advances every 15 days. `phaseEndsAt` is the authoritative server target.
  const phaseEndsAt = backendState?.phaseEndsAt ? new Date(backendState.phaseEndsAt) : null;
  const serverPhase = backendState?.currentPhase || 1;
  const serverPrice = backendState?.currentPrice || PRESALE_PHASES[0]?.price || 0.0010;

  // Initialize amount to minDeposit ONCE when minDeposit becomes known.
  // Do NOT clamp on every amount change — that would prevent users from typing
  // any intermediate value (e.g. typing "15" goes through "1" which is < min).
  // Final enforcement happens at submit time + the Buy button is disabled when amount < minDeposit.
  useEffect(() => {
    setAmount((prev) => (prev <= 0 ? minDeposit : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDeposit]);

  // Decide what we're counting toward: official launch (if presale not started yet),
  // OR phase ends (once live). Using the hardcoded LAUNCH_DATE keeps the countdown
  // stable and immune to any backend transient state on first paint.
  const countdownTarget = !presaleStarted ? LAUNCH_DATE : phaseEndsAt;
  const countdown = useMemo(() => {
    if (!countdownTarget) return { days: 0, hours: 0, mins: 0, secs: 0 };
    const diff = Math.max(0, Math.floor((countdownTarget.getTime() - now.getTime()) / 1000));
    return {
      days: Math.floor(diff / 86400),
      hours: Math.floor((diff % 86400) / 3600),
      mins: Math.floor((diff % 3600) / 60),
      secs: diff % 60,
    };
  }, [countdownTarget, now]);

  // Fall back to legacy local helper only if backend hasn't responded yet (initial render)
  const localStatus = useMemo(() => getPresalePhaseStatus(now), [now]);
  const status = backendState
    ? { state: presaleStarted ? 'live' : (promoActive ? 'fomo' : 'pre'), currentPhase: serverPhase, target: countdownTarget }
    : localStatus;
  const isLive = status.state === 'live';
  const isFomo = status.state === 'fomo';

  const phaseIdx = serverPhase - 1;
  const currentPrice = serverPrice;
  const bonusPercent = getBonusPercent(amount);
  const nextTier = getNextTier(amount);

  const baseTokens = amount / currentPrice;
  const bonusTokens = baseTokens * (bonusPercent / 100);
  const totalTokens = baseTokens + bonusTokens;

  // Real backend sold tokens per phase (used by the visual counter + reactive bumps)
  const tokensSoldByPhase = backendState?.tokensSoldByPhase || {};

  // ===== PHASE 1 HARDCODED END (frontend display) =====
  // Fixed target: July 25, 2026 04:00 UTC. Used for the interactive countdown
  // inside the "Prix du jour" block. Backend phase timing (15d cycles) is
  // preserved as-is; this is display-only.
  const PHASE1_ENDS_AT = useMemo(() => new Date('2026-07-25T04:00:00Z'), []);
  const phase1Countdown = useMemo(() => {
    const diff = Math.max(0, Math.floor((PHASE1_ENDS_AT.getTime() - now.getTime()) / 1000));
    return {
      days: Math.floor(diff / 86400),
      hours: Math.floor((diff % 86400) / 3600),
      mins: Math.floor((diff % 3600) / 60),
      secs: diff % 60,
    };
  }, [PHASE1_ENDS_AT, now]);

  // ===== VISUAL TOKENS-SOLD COUNTER (FRONTEND ONLY) =====
  // Backend still processes real 1:1 purchases. Frozen at page load so the number
  // does NOT tick every second (avoids erratic flicker). It bumps ONLY when the
  // backend reports a new real purchase (tokensSoldByPhase increases).
  //
  // Per-phase auto-growth curves (1% of 100M cap = 1 000 000 DOMIA per day):
  //   - Phase 1: 25 days → max 25M (25%)
  //   - Phase 2: 15 days → max 15M (15%)
  //   - Phase 3: 15 days → max 15M (15%)
  //   - Phase 4: 15 days → max 15M (15%)
  const PHASE1_CAP = 100_000_000;
  const PHASE_DURATIONS_DAYS = useMemo(() => [25, 15, 15, 15], []);
  const [pageLoadTime] = useState(() => new Date());

  const displayTokensSold = useMemo(() => {
    const BASE = 200_000;
    const DAILY_INCREMENT = 1_000_000; // 1% of 100M per day

    // Compute the start date of the CURRENT phase = LAUNCH_DATE + sum of previous durations
    let daysBeforeCurrent = 0;
    for (let i = 0; i < phaseIdx; i++) daysBeforeCurrent += PHASE_DURATIONS_DAYS[i] || 15;
    const phaseStart = new Date(LAUNCH_DATE.getTime() + daysBeforeCurrent * 86400_000);

    const durationForPhase = PHASE_DURATIONS_DAYS[phaseIdx] || 15;
    const MAX_AUTO = durationForPhase * DAILY_INCREMENT;

    // Frozen at page load — no per-second ticking
    const secondsSincePhaseStart = Math.max(0, (pageLoadTime.getTime() - phaseStart.getTime()) / 1000);
    const daysSincePhaseStart = Math.floor(secondsSincePhaseStart / 86400);
    const autoGrown = Math.min(MAX_AUTO, daysSincePhaseStart * DAILY_INCREMENT);

    // Real backend-sold DOMIA for the CURRENT phase (bumps live on real purchases via polling)
    const currentPhaseKey = status.currentPhase;
    const realSold = Number(tokensSoldByPhase[currentPhaseKey] || tokensSoldByPhase[String(currentPhaseKey)] || 0);

    return BASE + autoGrown + realSold;
  }, [pageLoadTime, LAUNCH_DATE, tokensSoldByPhase, phaseIdx, status.currentPhase, PHASE_DURATIONS_DAYS]);
  const displayProgress = Math.min(100, (displayTokensSold / PHASE1_CAP) * 100);

  // Adjacent phase prices for the horizontal price cards
  const currentPhaseData = PRESALE_PHASES[phaseIdx] || PRESALE_PHASES[0];
  const prevPhaseData = phaseIdx > 0 ? PRESALE_PHASES[phaseIdx - 1] : null;
  const nextPhaseData = PRESALE_PHASES[phaseIdx + 1] || null;

  const presetAmounts = [10, 20, 30, 50, 100, 1000];

  return (
    <section id="presale" className="relative z-10 max-w-[1400px] mx-auto px-6 scroll-mt-20">
      <div className="glass-card p-7 lg:p-9">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT */}
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-2 text-[12px] font-bold text-emerald-600">
                <span className={`w-2.5 h-2.5 rounded-full ${isLive ? 'bg-emerald-500' : isFomo ? 'bg-amber-500' : 'bg-blue-500'} pulse-anim`} />
              </span>
              <h2 className="text-3xl font-black tracking-tight text-slate-900">
                {isLive ? t.presale.live : isFomo ? 'PRESALE OPENS SOON' : 'PRESALE COMING SOON'}
              </h2>
              <span className="text-[11px] font-extrabold text-white bg-gradient-to-r from-blue-700 to-blue-500 rounded-full px-3 py-1">
                PHASE {status.currentPhase}
              </span>
            </div>

            {/* Top countdown block — only visible BEFORE presale is live.
                When Phase 1 is live, the countdown is shown ONLY inside the blue
                "Prix Actuel" card below (no more duplicate). */}
            {!isLive && (
            <div className={`mt-5 rounded-2xl p-5 ${isFomo ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-300 shadow-[0_8px_22px_rgba(245,158,11,0.18)]' : 'bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200'}`}>
              <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] text-slate-600">
                <Clock className={`w-3.5 h-3.5 ${isFomo ? 'text-amber-600' : 'text-blue-600'}`} />
                {isFomo ? '🔥 LAUNCH IN' : 'PRESALE STARTS IN'}
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[
                  { v: countdown.days, l: 'DAYS' },
                  { v: countdown.hours, l: 'HOURS' },
                  { v: countdown.mins, l: 'MIN' },
                  { v: countdown.secs, l: 'SEC' },
                ].map((x, i) => (
                  <div key={i} className={`rounded-xl bg-white border ${isFomo ? 'border-amber-200' : 'border-slate-200'} px-2 py-2.5 text-center`}>
                    <div className={`text-2xl md:text-3xl font-black ${isFomo ? 'text-amber-700' : 'text-slate-900'}`}>{String(x.v).padStart(2, '0')}</div>
                    <div className="text-[9px] font-bold tracking-[0.16em] text-slate-500">{x.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 text-center">
                Launch: <span className="font-bold text-slate-800">July 1, 2026 · 04:00 UTC</span>
              </div>
            </div>
            )}

            {/* ==== TOKENS SOLD (big display) ==== */}
            <div className="mt-6">
              <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500">TOKENS VENDUS · PHASE 1</div>
              <div className="mt-1 text-4xl md:text-[2.75rem] font-black text-slate-900 leading-none" data-testid="tokens-sold-display">
                {Math.floor(displayTokensSold).toLocaleString('fr-FR')}
                <span className="text-slate-400 text-2xl font-bold"> / {PHASE1_CAP.toLocaleString('fr-FR')} $DOMIA</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="progress-track neon-track flex-1" data-testid="presale-progress-track">
                <div
                  className="progress-fill neon-fill"
                  style={{ width: `${displayProgress}%`, transition: 'width 900ms cubic-bezier(0.4, 0, 0.2, 1)' }}
                  data-testid="presale-progress-fill"
                />
              </div>
              <div className="text-blue-600 font-extrabold tabular-nums" data-testid="presale-progress-percent">{displayProgress.toFixed(2)}%</div>
            </div>

            {/* ==== HORIZONTAL PRICE CARDS (previous / current / next) ==== */}
            <div className="mt-6 space-y-2.5">
              {/* Previous price — grey / closed */}
              <div className="rounded-xl bg-slate-100 border border-slate-200 px-4 py-2.5 flex items-center justify-between opacity-70" data-testid="price-block-previous">
                <div className="text-[11px] font-bold tracking-[0.16em] text-slate-500">PRIX PRÉCÉDENT</div>
                <div className="flex items-center gap-2">
                  <span className="text-[16px] font-black text-slate-500 line-through">${(prevPhaseData?.price ?? 0.0008).toFixed(4)}</span>
                  <span className="text-[10px] font-extrabold tracking-widest text-slate-400 bg-slate-200 rounded-full px-2 py-0.5">FERMÉ</span>
                </div>
              </div>

              {/* Current price — active blue block with countdown (larger for mobile readability) */}
              <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-5 sm:py-4 shadow-[0_10px_28px_rgba(37,99,235,0.4)] relative overflow-hidden" data-testid="price-block-current">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Clock className="w-5 h-5 text-amber-300 shrink-0" />
                    <span className="text-[11px] sm:text-[10px] font-black tracking-[0.16em] text-amber-200">SE TERMINE DANS</span>
                    <span className="text-[17px] sm:text-[15px] font-black tabular-nums text-amber-100" data-testid="phase1-countdown">
                      {String(phase1Countdown.days).padStart(2, '0')}j&nbsp;
                      {String(phase1Countdown.hours).padStart(2, '0')}h&nbsp;
                      {String(phase1Countdown.mins).padStart(2, '0')}m&nbsp;
                      {String(phase1Countdown.secs).padStart(2, '0')}s
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold tracking-[0.14em] text-blue-100">PRIX ACTUEL</span>
                    <span className="text-[26px] sm:text-[24px] font-black leading-none">${currentPhaseData.price.toFixed(4)}</span>
                    <span className="text-[10px] font-extrabold tracking-widest text-emerald-100 bg-emerald-500/30 border border-emerald-300/40 rounded-full px-2 py-0.5">EN COURS</span>
                  </div>
                </div>
              </div>

              {/* Next price — preview */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-2.5 flex items-center justify-between" data-testid="price-block-next">
                <div className="text-[11px] font-bold tracking-[0.16em] text-slate-500">PROCHAIN PRIX (PHASE {(phaseIdx + 2)})</div>
                <div className="flex items-center gap-2">
                  <span className="text-[16px] font-black text-slate-700">${(nextPhaseData?.price ?? 0.0015).toFixed(4)}</span>
                  <span className="text-[10px] font-extrabold tracking-widest text-blue-500 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">S&apos;OUVRE BIENTÔT</span>
                </div>
              </div>
            </div>

            {/* Phases — smaller professional strip */}
            <div className="mt-5 grid grid-cols-4 md:grid-cols-5 gap-1.5">
              {PRESALE_PHASES.map((p) => (
                <div key={p.phase} className={`rounded-lg border px-2 py-1.5 text-center ${p.phase === status.currentPhase && isLive ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200'}`}>
                  <div className="text-[9px] font-bold tracking-[0.12em] text-slate-500">PH {p.phase}</div>
                  <div className={`mt-0.5 text-[11px] font-black leading-none ${p.phase === status.currentPhase && isLive ? 'text-blue-600' : 'text-slate-900'}`}>${p.price.toFixed(4)}</div>
                </div>
              ))}
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-center">
                <div className="text-[9px] font-bold tracking-[0.12em] text-amber-600">LISTING</div>
                <div className="mt-0.5 text-[10px] font-black text-amber-700 leading-none">$0.003+</div>
              </div>
            </div>

            {/* Payment methods */}
            <div className="mt-7">
              <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500 mb-3">{t.presale.payment}</div>
              <div className="flex flex-wrap gap-2">
                {PAY_CURRENCIES.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
                    {m.icon ? (
                      <img src={m.icon} alt={`${m.label} ${m.chain}`} className="w-7 h-7 rounded-full object-contain" />
                    ) : (
                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: m.color }}>
                        {m.label === 'USDC' ? '$' : 'T'}
                      </span>
                    )}
                    <div className="leading-tight">
                      <div className="text-[11px] font-extrabold text-slate-900">{m.label}</div>
                      <div className="text-[10px] text-slate-500">{m.chain}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT - Deposit form */}
          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500">{t.presale.amount}</div>
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-2">
              {presetAmounts.map((v) => (
                <button key={v} onClick={() => setAmount(v)} data-testid={`preset-amount-${v}`} className={`rounded-full py-2.5 text-[13px] font-extrabold border transition ${amount === v ? 'bg-blue-600 text-white border-blue-600 shadow-[0_8px_18px_rgba(37,99,235,0.35)]' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}>${v}</button>
              ))}
              <button onClick={() => setAmount(4000)} data-testid="preset-amount-max" className={`rounded-full py-2.5 text-[12px] font-extrabold border transition ${amount === 4000 ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-600 border-amber-200 hover:border-amber-400'}`}>MAX</button>
            </div>

            <div className="mt-5 text-[11px] font-bold tracking-[0.18em] text-slate-500">CUSTOM AMOUNT (USD)</div>
            <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 sm:gap-3 max-w-full">
              <input
                type="number"
                min="0"
                value={amount === 0 ? '' : amount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || v === '0') return setAmount(0);
                  // Strip any leading zero so "050" → 50 cleanly
                  const cleaned = v.replace(/^0+/, '');
                  setAmount(Math.max(0, Number(cleaned) || 0));
                }}
                placeholder="0"
                data-testid="custom-amount-input"
                className="min-w-0 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-lg font-extrabold text-slate-900 focus:outline-none focus:border-blue-400"
              />
              <button onClick={() => setCurrency(currency === 'USDC' ? 'USDT' : 'USDC')} className="shrink-0 bg-white border border-slate-200 rounded-2xl px-3 sm:px-4 flex items-center gap-1.5 sm:gap-2 hover:border-blue-300" data-testid="currency-toggle">
                <img
                  src={currency === 'USDC' ? USDC_ICON : USDT_ICON}
                  alt={currency}
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-contain"
                />
                <span className="font-extrabold text-slate-900 text-[13px] sm:text-base">{currency}</span>
                <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
              </button>
            </div>

            {/* Professional bonus indicator */}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.16em] text-slate-500">PURCHASE BONUS</div>
                  <div className="text-[12px] text-slate-600 mt-0.5">Based on your deposit amount</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-slate-900 leading-none">+{bonusPercent}<span className="text-xl text-slate-400">%</span></div>
                  <div className={`text-[10px] font-bold tracking-wide mt-0.5 ${bonusPercent > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {bonusPercent > 0 ? 'TIER ACTIVE' : 'NO BONUS YET'}
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-slate-50/60">
                {nextTier && nextTier.bonus > bonusPercent ? (
                  <>
                    <div className="flex items-center justify-between text-[11px] mb-2">
                      <span className="font-bold text-slate-600">Next tier: <span className="text-slate-900">+{nextTier.bonus}%</span></span>
                      <span className="font-bold text-slate-500">${nextTier.min.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-300" style={{ width: `${Math.min(100, (amount / nextTier.min) * 100)}%` }} />
                    </div>
                    <div className="mt-2 text-[11px] text-slate-600">
                      Deposit <span className="font-extrabold text-blue-700">${Math.max(0, nextTier.min - amount).toLocaleString()}</span> more to unlock
                    </div>
                  </>
                ) : bonusPercent >= 30 ? (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-slate-600">Maximum tier reached</span>
                    <span className="text-[11px] font-extrabold tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">VIP TIER</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-500">No active bonus tier yet. Deposit at least $50 to unlock <span className="font-extrabold text-emerald-600">+5%</span> bonus tokens.</div>
                )}
              </div>
            </div>

            <div className="mt-5 text-[11px] font-bold tracking-[0.18em] text-slate-500">{t.presale.receive}</div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-3xl font-black text-slate-900">
                {totalTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                <span className="text-slate-400 text-xl ml-1">DOMIA</span>
              </div>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-amber-300 flex items-center justify-center text-[10px] font-black text-slate-900 border-2 border-amber-200">DA</div>
            </div>
            {bonusPercent > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                {baseTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} base + <span className="font-extrabold text-amber-600">{bonusTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} bonus</span>
              </div>
            )}

            {/* Promo banner: $0.50 minimum for 24h */}
            {promoActive && promoEndsAt && (
              <div className="mt-4 rounded-2xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 px-4 py-3 shadow-[0_8px_22px_rgba(245,158,11,0.18)]" data-testid="promo-banner">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
                    <div>
                      <div className="text-[10px] font-black tracking-[0.18em] text-amber-700">🔥 LAUNCH PROMO — 24H ONLY</div>
                      <div className="text-[13px] font-extrabold text-slate-900 mt-0.5">
                        Buy DOMIA from <span className="text-amber-700">${backendState?.promo_min_usd?.toFixed(2) || '0.50'}</span> only!
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] font-bold tracking-widest text-amber-700">ENDS IN</div>
                    <PromoCountdown endsAt={promoEndsAt} now={now} />
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-1.5">After promo: minimum deposit ${backendState?.promo_default_min_usd?.toFixed(0) || '10'}.</div>
              </div>
            )}

            <div className="relative mt-6">
              <span className="btn-ring" />
              <button
                onClick={() => setDepositOpen(true)}
                disabled={amount < minDeposit || (!presaleStarted && !promoActive)}
                data-testid="deposit-now-btn"
                className="btn-blue-glow w-full flex items-center justify-center gap-3 text-[15px] disabled:opacity-50"
                title={!presaleStarted && !promoActive ? `Opens July 1, 2026 · 00:00 UTC` : ''}
              >
                <Lock className="w-5 h-5" />
                {(!presaleStarted && !promoActive)
                  ? `OPENS ${launchLabel}`
                  : t.presale.depositNow}
              </button>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-2" data-testid="min-deposit-banner">
              <Lock className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-[12px] font-extrabold text-blue-700 tracking-wide">
                Minimum deposit: ${minDeposit < 10 ? minDeposit.toFixed(2) : minDeposit.toFixed(0)} · Sans KYC
              </span>
            </div>
          </div>
        </div>
      </div>

      <DepositModal open={depositOpen} onClose={() => setDepositOpen(false)} amountUsd={amount} bonusPercent={bonusPercent} estimatedTokens={totalTokens} />
      <SupportButton />
    </section>
  );
};

export default Presale;
